import { registerPlugin } from '@capacitor/core';

import { baueQrTeile, kannQrLesen, scanneImBrowser, type QrWebTeile } from './qrScannerWeb';

/**
 * Die Brücke zur eigenen Scanbühne (`ios/App/App/QrScannerPlugin.swift`).
 *
 * **Eigenbau statt `@capacitor/barcode-scanner`** (Entscheidung des Nutzers, 2026-09-14). Dessen
 * Oberfläche hält das Sichtfenster nur auf dem iPhone im Hochformat quadratisch — auf dem iPad
 * füllt es die ganze Fläche und dreht mit —, und sie kennt keine Frist: das Fremdplugin hat nur
 * `scanBarcode` und kein `stopScan`, sein Fenster bleibt bis zum Fund oder Tipp offen. Beides
 * war eine Vorgabe. Die Begründung im Langen steht in `QrScannerViewController.swift`.
 */

export interface QrScanErgebnis {
  /** Der gelesene Text. Fehlt bei Abbruch und abgelaufener Frist. */
  value?: string;
  /** Der Prüfer hat abgebrochen. */
  cancelled?: boolean;
  /** Die Frist ist abgelaufen, das Fenster hat sich von selbst geschlossen. */
  timedOut?: boolean;
}

export interface QrScannerPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  scan(optionen: { instructions?: string; timeoutSeconds?: number }): Promise<QrScanErgebnis>;
}

/**
 * Im Browser über `BarcodeDetector` — s. `platform/qrScannerWeb.ts`.
 *
 * ⚠️ **Hier stand bis zum 2026-09-22 „bewusst kein Web-Scanner".** Die Begründung war, ein
 * Ersatz wäre fremder Quelltext im Bündel (Prämisse des Nutzers). Das trifft auf `zxing` und
 * `jsQR` zu, nicht auf `BarcodeDetector`: das ist eine Browser-API. Für das **Gerät** ändert
 * sich nichts — iOS-WebKit hat sie nicht, dort bleibt es bei der nativen Bühne.
 */
export class QrScannerWeb implements QrScannerPlugin {
  private readonly teile: QrWebTeile;

  constructor(teile: Partial<QrWebTeile> = {}) {
    this.teile = baueQrTeile(teile);
  }

  async isAvailable() {
    const hatKamera = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
    return { available: hatKamera && (await kannQrLesen()) };
  }

  async scan(optionen: { instructions?: string; timeoutSeconds?: number }): Promise<QrScanErgebnis> {
    return scanneImBrowser(optionen, this.teile);
  }
}

export const QrScanner = registerPlugin<QrScannerPlugin>('QrScanner', {
  web: () => new QrScannerWeb(),
});
